import { createSelector } from "reselect";
import {
	FACEBOOK_DSP_ID,
	FACEBOOK_DSP_INTERNAL_NAME,
	YOUTUBE_DSP_INTERNAL_NAME,
	PLATFORMS
} from "utils/constants";

function getFacebookPages(state){
	return state.social?.facebookPages || { promotePages:[], instagramAccounts:[]};
}

function getSocialValuePlacements(state){
	const {
		placementAssetCustomizationType,
		placements,
	} = state.creativeDetails?.socialValues || {};

	let isPacEnabled = placementAssetCustomizationType && placementAssetCustomizationType !== "none";
	return (isPacEnabled && Array.isArray(placements)) ? placements : undefined;
}

function getFacebookImageAdTypes(state){
	let types = [];
	try {
		types = state.social?.metadata[FACEBOOK_DSP_ID].facebookImagePreviewAdTypes || [];
	} catch (err){}

	return types;
}

function getFacebookVideoAdTypes(state){
	let types = [];
	try {
		types = state.social?.metadata[FACEBOOK_DSP_ID]?.facebookVideoPreviewAdTypes || [];
	} catch (err) {}

	return types;
}

function getFacebookCarouselAdTypes(state) {
	let types = [];
	try {
		types = state.social?.metadata?.[FACEBOOK_DSP_ID]?.carouselPreviewAdTypes || [];
	} catch(err) {}

	return types;
}

function getSelectedTab(state){
	return state.social?.selectedTab?.value || "facebook" ;
}

function compilePlacements(previewAdTypes, socialValuePlacements = [], selectedTab){
	let placementOptions = [];

	if(selectedTab === "generic"){
		const socialValuePlacementsOptions = socialValuePlacements
			.filter(item => item.status === "active")
			.map((item) => {
				return {
					id: item.type?.externalId,
					description: `${item.type.name} (${item.type.description})`,
				};
			});

		placementOptions = [
			{
				id: "ft_aspect_default",
				description: "Default Assets",
			},
			...socialValuePlacementsOptions,
		];
	} else if(selectedTab === "facebook"){
		placementOptions = previewAdTypes.filter(item => !/instagram|messenger/gi.test(item.id));
	} else if(selectedTab === "instagram" || selectedTab === "messenger"){
		placementOptions =  previewAdTypes.filter((plc) => {
			let tabFilter = new RegExp(selectedTab, 'gi');
			return tabFilter.test(plc.id);
		});
	}

	return placementOptions.map((opt) => {
		return {
			value:opt.id,
			label:opt.description,
		};
	});
}

function buildPageSelects(facebookInfo){
	return {
		facebookPages:facebookInfo.promotePages?.map((page) => {
			return {
				...page,
				value:page.id,
				label:page.name,
			};
		}) || [],
		instagramAccounts:facebookInfo.instagramAccounts?.map((acc) => {
			return {
				...acc,
				value:acc.id,
				label:acc.username,
			};
		}) || [],
	};
}

function setUnionArray(sets){
	let allItems = sets.flat();
	let set = new Set(allItems);
	return [...set];
}

function combineSelections(selections){
	/*
		`selections` is an array of selection objects in the form:
			{
				platforms: [],
				formats: [],
			}
		Returns a minimal set containing all unique values in `platforms` and `formats`, in the same
		format as a single object in `selections`.
	*/

	// This isn't the most efficient, but these are short arrays.
	let platformSelections = selections.map(item => item.platforms);
	let formatSelections = selections.map(item => item.formats);

	return {
		platforms: setUnionArray(platformSelections),
		formats: setUnionArray(formatSelections),
	};
}


function getCreativeValues(state){
	return state.creativeDetails?.values;
}

/*
	Social Creatives have properties in a format like:
		{
			values: {
				headline: {
					value: "Some headline",
					conceptValue: "Some headline",
					isInherited: true,
				}
			}
		}
*/
function editCreativeInitialValues(values = {}){
	/*
		Our form just collects the values, flat:
			{
				headline: "Some headline",
			}
		So we have to unpack the Creative model for our `initialValues`
	*/
	let valueEntries = Object.entries(values || {});
	let initialValues = {};

	for(let [key, value] of valueEntries){
		initialValues[key] = value?.value;
	}

	return initialValues;
}

function editCreativeConceptValues(values = {}){
	/*
		Compile just the `conceptValue` values from the Social Creative.

		We use `conceptValues` to tell fields that can inherit from the Concept what value to inherit,
		and what value to compare against when deciding whether to show the revert to parent value button.
	*/
	let valueEntries = Object.entries(values || {});
	let conceptValues = {};

	for(let [key, value] of valueEntries){
		conceptValues[key] = value?.conceptValue;
	}

	return conceptValues;
}

function getDSP(state){
	return state.social?.items || [];
}

function extractFacebookDSP(items){
	// There should only ever be one of these, so we can just use a regular `find`.
	let facebookDSP = items.find(item => item?.dsp?.internalName === FACEBOOK_DSP_INTERNAL_NAME);
	if(facebookDSP){
		return facebookDSP.id;
	}
	return -1;
}

function compileConnections(hasFacebook, hasYouTube){
	let platforms = [];
	if(hasFacebook){
		platforms.push(PLATFORMS.FACEBOOK);
	}
	if(hasYouTube){
		platforms.push(PLATFORMS.YOUTUBE);
	}

	return platforms;
}

function compilePreviewPlatformTabs(previewAdTypes) {
	const tabOptionsRegExp = /facebook|instagram|messenger|youtube/i;

	return previewAdTypes.reduce((arr, { description }) => {
		const option = description.match(tabOptionsRegExp)[0].toLowerCase();
		return arr.includes(option) ? arr : arr.concat(option);
	}, []);
}

export const activeSocialValuePlacements = createSelector([getSocialValuePlacements], placements => placements.filter(item => item.status === "active"));
export const buildImagePlacements = createSelector([getFacebookImageAdTypes, getSocialValuePlacements, getSelectedTab], compilePlacements);
export const buildVideoPlacements = createSelector([getFacebookVideoAdTypes, getSocialValuePlacements, getSelectedTab], compilePlacements);
export const buildCarouselPlacements = createSelector([getFacebookCarouselAdTypes, getSocialValuePlacements, getSelectedTab], compilePlacements);
export const buildCarouselPreviewTabs = createSelector([getFacebookCarouselAdTypes], compilePreviewPlatformTabs);
export const buildVideoPreviewTabs = createSelector([getFacebookVideoAdTypes], compilePreviewPlatformTabs);
export const buildImagePreviewTabs = createSelector([getFacebookImageAdTypes], compilePreviewPlatformTabs);

export const buildFacebookPages = createSelector([getFacebookPages], buildPageSelects);

export const buildEditCreativeInitialValues = createSelector([getCreativeValues], editCreativeInitialValues);
export const buildEditCreativeConceptValues = createSelector([getCreativeValues], editCreativeConceptValues);

// DSP Connections
export const hasSocialConnections = createSelector([getDSP], dsps => dsps.length > 0);
export const hasFacebookConnection = createSelector([getDSP], dsps => dsps.some(item => item.dsp.internalName === FACEBOOK_DSP_INTERNAL_NAME));
export const hasYouTubeConnection = createSelector([getDSP], dsps => dsps.some(item => item.dsp.internalName === YOUTUBE_DSP_INTERNAL_NAME));
export const connectedPlatforms = createSelector([hasFacebookConnection, hasYouTubeConnection], compileConnections);
export const getFacebookDSPConnectionId = createSelector([getDSP], extractFacebookDSP);

export const PRIVATE = {
	getFacebookPages,
	getSocialValuePlacements,
	getFacebookImageAdTypes,
	getFacebookVideoAdTypes,
	compilePlacements,
	buildPageSelects,
	combineSelections,
	setUnionArray,
	getCreativeValues,
	editCreativeInitialValues,
	editCreativeConceptValues,

	hasSocialConnections,
	hasFacebookConnection,
	hasYouTubeConnection,
	connectedPlatforms,
	getFacebookDSPConnectionId,
};
